/**
 * MED-876 — Client for the 2 AI compose endpoints used by the Session Prep page.
 *
 * - POST /api/ai/prep-suggestions       (per-step, fired on segment expand)
 * - POST /api/ai/pre-session-guidance   (per group tab, fired on mount)
 *
 * Both responses are wrapped in `ComposeEnvelope<T>`. The UI never branches on
 * `metadata.is_mock` — only on `status` (`'ok' | 'mock'` are rendered identically;
 * `'fail_open'` triggers the empty state). HTTP 503 `AI_ENGINE_NOT_WIRED` is
 * normalized to a synthetic fail_open envelope so callers have ONE branching rule.
 */

import apiClient from './client';
import type {
  ComposeEnvelope,
  ComposeMetadata,
  PreSessionGuidanceOutput,
  PreSessionGuidanceRequest,
  PrepSuggestionsOutput,
  PrepSuggestionsRequest,
  RecapDraftOutput,
  RecapDraftRequest,
} from './aiTypes';

/**
 * Synthesize a `fail_open` envelope locally — used when the server returns
 * HTTP 503 (`AI_ENGINE_NOT_WIRED`), so the UI can treat it identically to a
 * server-returned `fail_open`.
 */
function syntheticFailOpen<T>(): ComposeEnvelope<T> {
  const metadata: ComposeMetadata = {
    recipe_version: 'unwired',
    generated_at: new Date().toISOString(),
    latency_ms: 0,
    is_mock: false,
    judge_score: null,
    judge_critical_issues: [],
    request_id: 'client-synth-fail-open',
  };
  return { status: 'fail_open', result: null, metadata };
}

function isAiEngineNotWired(err: unknown): boolean {
  // The axios interceptor in services/api/client.ts flattens API errors to
  // Error & { status, code, meta } — there is no `err.response`. Reading the
  // nested shape (the old pattern) always returned undefined, so this helper
  // silently never matched. Read the flat properties directly.
  const apiErr = err as Error & { status?: number; code?: string };
  return apiErr.status === 503 && apiErr.code === 'AI_ENGINE_NOT_WIRED';
}

/**
 * MED-1019 — every compose runs a recipe + an inline judge on Bedrock (Sonnet),
 * which legitimately takes ~15-25s and more under slower/cold conditions. The
 * apiClient's 30s instance default is too tight for that and caused valid
 * composes to spuriously time out — surfacing as the "Generate More Prompts"
 * spinner that "never returns" (e.g. the in-session ActiveSessionGuidePanel).
 * These calls override it. The timeout still GUARANTEES the promise settles, so
 * the caller's loading state always resolves (success or a catchable error) —
 * it never hangs forever.
 */
const AI_COMPOSE_TIMEOUT_MS = 60_000;

export const aiApi = {
  /**
   * AC2: prep suggestions for one agenda segment. The mock layer's fixtures
   * are deterministic by `session_id` hash — same session_id always returns
   * the same fixture. Use `session_id = "sess-z"` to force a fail_open fixture
   * for testing empty-state rendering (see medley-api/services/ai/mocks/).
   */
  async prepSuggestions(
    body: PrepSuggestionsRequest,
  ): Promise<ComposeEnvelope<PrepSuggestionsOutput>> {
    try {
      return await apiClient.post<ComposeEnvelope<PrepSuggestionsOutput>>(
        '/api/ai/prep-suggestions',
        body,
        { timeout: AI_COMPOSE_TIMEOUT_MS },
      );
    } catch (err) {
      if (isAiEngineNotWired(err)) return syntheticFailOpen<PrepSuggestionsOutput>();
      throw err;
    }
  },

  /**
   * AC5: pre-session sentiment & guidance for one group. Fires on Session Prep
   * page mount per group tab; per IV5 the call MUST be non-blocking so the rest
   * of the page renders independently.
   */
  async preSessionGuidance(
    body: PreSessionGuidanceRequest,
  ): Promise<ComposeEnvelope<PreSessionGuidanceOutput>> {
    try {
      return await apiClient.post<ComposeEnvelope<PreSessionGuidanceOutput>>(
        '/api/ai/pre-session-guidance',
        body,
        { timeout: AI_COMPOSE_TIMEOUT_MS },
      );
    } catch (err) {
      if (isAiEngineNotWired(err)) return syntheticFailOpen<PreSessionGuidanceOutput>();
      throw err;
    }
  },

  /**
   * MED-893 AC8: recap email draft. Fires on Coach Post-Session page mount
   * when `live_session_delivery` is on. Pre-populates the SendRecapEmailModal
   * body (subject, greeting, key themes, what worked, individual commitments
   * with source_quote, carryover, closing) and feeds AC7's Medley Feedback
   * Form `session_insights` (re-targeted from `session_themes` in MED-988) and
   * `carryover_for_next_session` fields. Mock fixture
   * `session_id = "sess-z"` forces a fail_open envelope for empty-state testing.
   */
  async recapDraft(body: RecapDraftRequest): Promise<ComposeEnvelope<RecapDraftOutput>> {
    try {
      return await apiClient.post<ComposeEnvelope<RecapDraftOutput>>(
        '/api/ai/recap-draft',
        body,
        { timeout: AI_COMPOSE_TIMEOUT_MS },
      );
    } catch (err) {
      if (isAiEngineNotWired(err)) return syntheticFailOpen<RecapDraftOutput>();
      throw err;
    }
  },
};
